import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  student_id: Yup.number().required("This field is required"),
  cohort: Yup.string().required("This field is required"),
  section: Yup.string().required("This field is required"),
  registration_date: Yup.string().required("This field is required"),
  shift_days: Yup.string().required("This field is required"),
  shift_time: Yup.string().required("This field is required"),
  household_items: Yup.array()
    .of(Yup.string().required("Each household item must have a valid name"))
    .min(1, "At least one household item is required"),
  smartphone: Yup.string().required("This field is required"),
  laptopdesktop: Yup.string().required("This field is required"),
  currently_employed: Yup.string().required("This field is required"),
  position: Yup.string().test({
    test: function (value) {
      return this.parent.currently_employed === "Yes" ? !!value : true;
    },
    message: "This field is required",
  }),
  salary: Yup.string().test({
    test: function (value) {
      return this.parent.currently_employed === "Yes" ? !!value : true;
    },
    message: "This field is required",
  }),
  employer_name: Yup.string().test({
    test: function (value) {
      return this.parent.currently_employed === "Yes" ? !!value : true;
    },
    message: "This field is required",
  }),
  code_girls_training_related_work: Yup.string().required(
    "This field is required"
  ),
  code_girls_training_related_work_other: Yup.string().test({
    test: function (value) {
      return this.parent.code_girls_training_related_work === "Other"
        ? !!value
        : true;
    },
    message: "This field is required",
  }),
  interested_in_partner_opportunity: Yup.string().required(
    "This field is required"
  ),
  interested_in_partner_opportunity_other: Yup.string().test({
    test: function (value) {
      return this.parent.interested_in_partner_opportunity === "Other"
        ? !!value
        : true;
    },
    message: "This field is required",
  }),
  opportunity_preference: Yup.string().test({
    test: function (value) {
      return this.parent.interested_in_partner_opportunity === "Yes"
        ? !!value
        : true;
    },
    message: "This field is required",
  }),
  interested_in_advanced_phase: Yup.string().required(
    "This field is required"
  ),
  interested_in_advanced_phase_other: Yup.string().test({
    test: function (value) {
      return this.parent.interested_in_advanced_phase === "Other"
        ? !!value
        : true;
    },
    message: "This field is required",
  }),
  interested_in_events: Yup.string().required("This field is required"),
  interested_in_events_other: Yup.string().test({
    test: function (value) {
      return this.parent.interested_in_events === "Other" ? !!value : true;
    },
    message: "This field is required",
  }),
  prefer_freelancing: Yup.string().required("This field is required"),
  prefer_freelancing_other: Yup.string().test({
    test: function (value) {
      return this.parent.prefer_freelancing === "Other" ? !!value : true;
    },
    message: "This field is required",
  }),

  // New validation rules for post-assessment fields
  training_objective_achieved: Yup.string().required("This field is required"),
  recommend_consulnet: Yup.string().required("This field is required"),
  training_impact_personal_life: Yup.array()
    .of(Yup.string())
    .min(1, "At least one option must be selected"),
  post_training_activity: Yup.string().required("This field is required"),
  post_training_activity_other: Yup.string().test({
    test: function (value) {
      return this.parent.post_training_activity === "Other" ? !!value : true;
    },
    message: "This field is required",
  }),
  future_plans: Yup.string().required("This field is required"),
  employment_status: Yup.string().required("This field is required"),

  // Unemployment reasons - only required if unemployed
  unemployment_reasons: Yup.array().when("employment_status", {
    is: "I am unemployed",
    then: (schema) =>
      schema.of(Yup.string()).min(1, "At least one reason must be selected"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Employment fields - only required if employed
  number_of_jobs: Yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .nullable()
    .when("employment_status", {
      is: "I am employed",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  employment_relevance: Yup.string().when("employment_status", {
    is: "I am employed",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  total_experience_years: Yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .nullable()
    .when("employment_status", {
      is: "I am employed",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  tech_experience_years: Yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .nullable()
    .when("employment_status", {
      is: "I am employed",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  career_without_training: Yup.string().when("employment_status", {
    is: "I am employed",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  pay_better_than_before: Yup.string().when("employment_status", {
    is: "I am employed",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  pay_better_reason: Yup.string().test({
    test: function (value) {
      return this.parent.pay_better_than_before === "Yes" &&
        this.parent.employment_status === "I am employed"
        ? !!value
        : true;
    },
    message: "This field is required",
  }),

  // Freelancing fields - only required if freelancer
  freelancing_platforms: Yup.string().when("employment_status", {
    is: "I am a freelancer",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  freelancing_projects: Yup.string().when("employment_status", {
    is: "I am a freelancer",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  freelancing_income: Yup.string().when("employment_status", {
    is: "I am a freelancer",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  freelancing_technologies: Yup.string().when("employment_status", {
    is: "I am a freelancer",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  freelancing_help_from_training: Yup.string().when("employment_status", {
    is: "I am a freelancer",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Business fields - only required if business owner
  business_name: Yup.string().when("employment_status", {
    is: "I own a business",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  business_nature: Yup.string().when("employment_status", {
    is: "I own a business",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  business_sales_platforms: Yup.string().when("employment_status", {
    is: "I own a business",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  business_tech_enabled: Yup.string().when("employment_status", {
    is: "I own a business",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  business_duration: Yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .nullable()
    .when("employment_status", {
      is: "I own a business",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  business_monthly_revenue: Yup.string().when("employment_status", {
    is: "I own a business",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // General engagement fields
  engagement_feature_journey: Yup.string().required("This field is required"),
  engagement_community_roles: Yup.array()
    .of(Yup.string())
    .min(1, "At least one role must be selected"),
});

export default validationSchema;